/**
 * List of global variables used on our project.
 */

/**
 * Used for server request with specific delay time.
 */
export const SERVER_REQUEST_DELAY_MS = 1000;

/**
 * Used for working with time periods in milliseconds.
 */
export const DAY_DURATION_MS = 86400000;

/**
 * Used for quick-filter component.
 */
export const DEFAULT_FILTER_DEBOUNCE_TIME_MS = 1000;

/**
 * Used for delaying form updating. E.g. We need to wait for user finished make changes in the form.
 */
export const DEFAULT_FORM_UPDATE_DELAY_MS = 500;

/**
 * Used for delaying error validation notifier.
 * E.g. We need to wait for user finished make changes in the form.
 */
export const ERROR_MESSAGE_DEBOUNCE = 1000;

/**
 * The time period after which we remove toaster notification.
 */
export const DEFAULT_TOAST_TIMEOUT = 7000;
export const DEFAULT_ERROR_TOAST_TIMEOUT = 6500;
export const DEFAULT_WARNING_TOAST_TIMEOUT = 15000;

/**
 * Used for delaying collapse animation. E.g. We need to wait for until animation is finished.
 */
export const COLLAPSE_ANIMATION_DURATION_MS = 300;

/**
 * Global week days, starts from Sunday
 */
export const WEEK_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const WEEK_DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/**
 * Global months, starts from January
 */
export const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

export const TIME_FORMAT = 'HH:mm:ss A';
export const TIME_12H_FORMAT = 'hh:mmA';
export const TIME_24H_FORMAT = 'HH:mm';

export const DATE_TIME_FORMAT = 'MM/DD/YYYY HH:mm:ss';
export const DATE_TIME_DETAIL_FORMAT = 'YYYY/MM/DD h:mm:ss.SSS';
export const DATE_TIME_DEFAULT_FORMAT = 'YYYY-MM-DD HH:mm:ss A';
export const DATE_FORMAT = 'MM/DD/YYYY';
export const CARD_EXPIRATION_DATE_FORMAT = 'MM/YYYY';
export const SHORT_DATE_FORMAT_WITH_LOCALE = 'L';
export const SHORT_TIME_FORMAT_WITH_LOCALE = 'LT';
export const LONG_TIME_FORMAT_WITH_LOCALE = 'LTS';
export const LONG_DATE_TIME_FORMAT_WITH_LOCALE = 'L LTS';
export const EN_LOCALE = 'en';

export const BUSINESS_DATE_FORMAT = 'YYYY-MM-DD';
export const MOMENT_FORMATS = [DATE_FORMAT, BUSINESS_DATE_FORMAT];
export const DEFAULT_ITEM_BACKGROUND_COLOR = '#eeeeee';
export const DEFAULT_ITEM_TEXT_COLOR = '#000000';

export const APP_CODE = 'XPOS';

export const MAX_IMAGE_DIMENSIONS = 512;
export const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
export const IMAGE_ACCEPT_OPTIONS = 'image/jpeg, image/jpg, image/png, image/gif';

export const IMAGE_ALIGN = ['left', 'center', 'right'];

export const addLocaleFormatToMoment = (format: string) => {
    MOMENT_FORMATS.push(format);
};
// default precision for numeric field
export const DEFAULT_NUMBER_PRECISION = 2;

export const TAG_TYPE_IDS = {
    INVENTORY: '58e5478117d69f437348867d',
    PRODUCTS: '59aebf86015ad619587552c9',
};

export const RATE_TYPE_IDS = {
    SHDIF: '58e5472417d69f437348866e',
    CNSDA: '58e546f717d69f4373488667',
};

export const PAY_CODE = {
    0: 'CUSTOM',
    1: 'CASH',
    2: 'CHECK',
    3: 'CREDIT',
    4: 'DEBIT',
    5: 'GIFTCC',
    6: 'GIFT',
    7: 'HOUSEA',
    8: 'SMARTC',
    9: 'EMPLCH',
    10: 'DELCH',
    11: 'FOODST',
    12: 'EBTFST',
    13: 'EBTC',
};

/**
 * List of permissions that are required to work with application
 */
export const APP_LEVEL_PERMISSIONS = ['preferences.View'];

/**
 * Default chunk size for infine scroll widgets
 */
export const DEFAULT_PAGE_SIZE = 50;
/**
 * Page size to be selected from on list container
 */
export const AVAILABLE_PAGE_SIZES: number[] = [10, 50, 100, 200];

/**
 * Availabe permission suffixes
 */
export const PERMISSION_TYPES = {
    Add: 'Add',
    Delete: 'Delete',
    Manage: 'Manage',
    View: 'View',
};

/**
 * Message with missed permissions
 */
export const MISSED_PERMISSIONS_MESSAGE = `You haven't the following permissions: `;

/**
 * Default recipe measure code.
 */
export const DEFAULT_RECIPE_MESURE_CODE = 'EA';

/**
 * Administrator permission
 */
export const ADMINISTRATOR_PERMISSION = 'Administrator';
export const REPORTING_ADMINISTRATOR_PERMISSION = 'Reporting.Admin';

export const ERRORS: any = {
    EXPIRED_TOKEN: 'Token is expired',
    INVALID_BUSINESS_DATE: 'No data for current company.',
    NO_BUSINESS_RULE_GROUPS: 'There are no business rule groups for. Please choose another site',
    NO_CALENDARS: 'There are no calendars for this site. Please choose another site',
    NO_JOB_CATEGORIES: 'There are no Job Categories for this site. Please choose another site',
};

export const SELECT_ALL = 'All';
export const NOTHING = 'Nothing';

export const CUSTOM_REPORTS: any = {
    BASE_BAR_CHART: {
        barChartOptions: {
            scaleShowVerticalLines: false,
            responsive: true,
        },
        barChartType: 'bar',
        barChartLegend: true,
        barChartLabels: [],
        barChartData: [],
    },
    HORIZONTAL_BAR_CHART: {
        barChartOptions: {
            scaleShowVerticalLines: false,
            responsive: true,
        },
        barChartType: 'horizontalBar',
        barChartLegend: true,
        barChartLabels: [],
        barChartData: [],
    },
};

export const FEATURES = {
    TAX_INCLUSIVE_PRICE: 'TAX_INCLUSIVE_PRICE',
    PORTAL_SITE_SELECTOR: 'PORTAL_SITE_SELECTOR',
};

export const HTTP_CACHE_TTL = 900000; // 15 minutes

export const PDF_SIGNATURE_LINE = '  _______________________________  ';

/**
 * 822.4 = 11.7 * 72 - 20
 * 11.7: A4 landscape width(inch)
 * 72: inch to pt
 * 20: pdf horizontal margin
 */
export const PDF_INITIAL_LANDSCAPE_WITH: number = 842.4;
export const PDF_INITIAL_HORIZONTAL_MARGIN: number = 20;
export const PDF_INITIAL_HORIZONTAL_MARGIN_XCAT: number = 14;
export const PDF_INITIAL_PADDING_FOR_TABLE_CELL: number = 10;

export enum PDF_EXPORT_FONTS {
    SourceSansPro = 'SourceSansPro',
}

export const PDF_DEFAULT_USE_FONT = PDF_EXPORT_FONTS.SourceSansPro;
export const PDF_TABLE_BODY_FONT_SIZE = 11;

export const REPORT_VIEW = {
    SITE_VIEW: 'Site View',
    COMPANY_VIEW: 'Company View',
};

export enum THEME_TYPE {
    XENIAL = 'xenial',
    GENIUS = 'genius',
}
// export const THEME_COLORS = {
//   WHITE_COLOR: '#FFFFFF',
//   BLUE_COLOR: '#0C79BF',
//   RED_COLOR: '#fc6374',
// };

export const EVENT_SELECTOR_SELECT = 'id,name,status,event_type_entity_id,event_days,event_day_ordering';
export const DEFAULT_LANGUAGE = 'en-US';
export const HTTP_SUCCESS_STATUS_LOWER_BOUND = 200;
export const HTTP_SUCCESS_STATUS_UPPER_BOUND = 300;

export const DEFAULT_SELECTED_ALL_OPTION = 'All';
